import { useState, useEffect } from "react";
import {
    Box,
    Typography,
    Slider,
    TextField,
    IconButton,
    Button,
    LinearProgress,
    Grid,
    CircularProgress
} from "@mui/material";
import { Add, Delete } from "@mui/icons-material";
import { useEditMode } from "../../context/EditModeContext";
import { useSupabaseTable } from "../../hooks/useSupabaseTable";

type Competency = {
    id: string | number; // string for temp IDs, number for saved IDs
    skill: string;
    level: number;
};

export default function CoreCompetency() {
    const { isEditMode } = useEditMode();

    const { data: supabaseItems, insert, update, remove } =
        useSupabaseTable<Competency>("core_competancy", { realtime: true });

    const [competencies, setCompetencies] = useState<Competency[]>([]);
    const [originalCompetencies, setOriginalCompetencies] = useState<Competency[]>([]);
    const [dirty, setDirty] = useState(false);
    const [removedCompetencyIds, setRemovedCompetencyIds] = useState<(number | string)[]>([]);
    const [saving, setSaving] = useState(false);

    useEffect(() => {
        if (supabaseItems) {
            setCompetencies(supabaseItems);
            setOriginalCompetencies(supabaseItems);
        }
    }, [supabaseItems]);

    const handleChange = (index: number, field: "skill" | "level", value: string | number) => {
        const newComps = [...competencies];
        newComps[index] = { ...newComps[index], [field]: field === "level" ? Number(value) : value };
        setCompetencies(newComps);
        setDirty(true);
    };

    const addCompetency = () => {
        setCompetencies([
            ...competencies,
            { id: `temp-${Date.now()}`, skill: "", level: 0 },
        ]);
        setDirty(true);
    };

    const removeCompetency = (index: number) => {
        const target = competencies[index];
        if (typeof target.id === "number") {
            setRemovedCompetencyIds(prev => [...prev, target.id]);
        }
        setCompetencies(competencies.filter((_, i) => i !== index));
        setDirty(true);
    };

    const handleSave = async () => {
        setSaving(true);

        try {
            // Delete removed competencies first (batch if possible)
            if (removedCompetencyIds.length) {
                await Promise.all(removedCompetencyIds.map(id => remove({ id })));
            }

            const insertOps = competencies
                .filter(c => typeof c.id === "string" && c.id.startsWith("temp"))
                .map(c => insert({ skill: c.skill, level: c.level }));

            const updateOps = competencies
                .filter(c => typeof c.id === "number")
                .map(c => update(c, { id: c.id }));

            await Promise.all([...insertOps, ...updateOps]);

            setOriginalCompetencies([...competencies]);
            setRemovedCompetencyIds([]);
            setDirty(false);
        } catch (err) {
            console.error("Error saving competencies:", err);
        } finally {
            setSaving(false);
        }
    };

    const handleCancel = () => {
        setCompetencies(originalCompetencies);
        setRemovedCompetencyIds([]);
        setDirty(false);
    };

    return (
        <Box sx={{ mt: 4 }}>
            <Box display="flex" justifyContent="space-between" alignItems="center">
                <Typography variant="h5" gutterBottom>
                    Core Competencies
                </Typography>
            </Box>

            <Grid container spacing={3}>
                {competencies.map((comp, index) => (
                    <Grid key={comp.id ?? index} size={{ xs: 12, md: 4 }}>
                        <Box
                            sx={{
                                mb: 3,
                                p: 2,
                                border: "1px solid #ddd",
                                borderRadius: 2,
                                backgroundColor: "#fafafa",
                            }}
                        >
                            {isEditMode ? (
                                <Box>
                                    <TextField
                                        label="Skill"
                                        value={comp.skill}
                                        onChange={(e) => handleChange(index, "skill", e.target.value)}
                                        fullWidth
                                        sx={{ mb: 2 }}
                                    />
                                    <Slider
                                        value={comp.level}
                                        onChange={(_, newValue) =>
                                            handleChange(index, "level", newValue as number)
                                        }
                                        step={1}
                                        min={0}
                                        max={100}
                                        valueLabelDisplay="on"
                                    />
                                    <IconButton
                                        color="error"
                                        onClick={() => removeCompetency(index)}
                                    >
                                        <Delete />
                                    </IconButton>
                                </Box>
                            ) : (
                                <Box>
                                    <Typography variant="subtitle1" fontWeight="bold">
                                        {comp.skill}
                                    </Typography>
                                    <LinearProgress
                                        variant="determinate"
                                        value={comp.level}
                                        sx={{
                                            height: 10,
                                            borderRadius: 5,
                                            mt: 1,
                                            backgroundColor: "#e0e0e0",
                                            "& .MuiLinearProgress-bar": {
                                                backgroundColor: "#1976D2",
                                            },
                                        }}
                                    />
                                </Box>
                            )}
                        </Box>
                    </Grid>
                ))}
            </Grid>

            {isEditMode && (
                <Button
                    startIcon={<Add />}
                    onClick={addCompetency}
                    variant="outlined"
                    sx={{ mt: 2 }}
                    disabled={saving}
                >
                    Add Competency
                </Button>
            )}

            {isEditMode && dirty && (
                <Box sx={{ mt: 2 }}>
                    <Button
                        variant="contained"
                        color="primary"
                        onClick={handleSave}
                        sx={{ mr: 1 }}
                        disabled={saving}
                    >
                        {saving ? <CircularProgress size={24} /> : "Save"}
                    </Button>
                    <Button variant="outlined" color="secondary" onClick={handleCancel} disabled={saving}>
                        Cancel
                    </Button>
                </Box>
            )}
        </Box>
    );
}
